import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { format, startOfWeek } from 'date-fns';

const EVENT_TYPE_META = {
  booking: { label: 'Bookings', color: 'bg-blue-500' },
  cleaning: { label: 'Cleaning', color: 'bg-emerald-500' },
  maintenance: { label: 'Maintenance', color: 'bg-amber-500' },
  landscaping: { label: 'Landscaping', color: 'bg-green-600' },
  renovation: { label: 'Renovations', color: 'bg-violet-600' },
  owner_use: { label: 'Owner Use', color: 'bg-rose-500' },
};

/**
 * CalendarToolbar — nav (prev/today/next), event-type legend,
 * property color key (multi-property views), and month/week view
 * switcher. Purely presentational — all state lives in the parent.
 */
export const CalendarToolbar = ({
  currentDate,
  viewMode,
  setViewMode,
  onPrev,
  onToday,
  onNext,
  properties,
  selectedProperty,
  propertyColorMap,
}) => {
  return (
    <Card className="bg-white border border-slate-100 p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPrev} data-testid="calendar-prev-btn">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={onToday} data-testid="calendar-today-btn">
          Today
        </Button>
        <Button variant="outline" size="sm" onClick={onNext} data-testid="calendar-next-btn">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <span className="ml-4 font-semibold text-slate-900">
          {viewMode === 'month'
            ? format(currentDate, 'MMMM yyyy')
            : `Week of ${format(startOfWeek(currentDate), 'MMM d, yyyy')}`}
        </span>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex flex-wrap items-center gap-3 mr-2 text-xs">
          {Object.entries(EVENT_TYPE_META).map(([key, meta]) => (
            <span key={key} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded ${meta.color}`}></div>
              {meta.label}
            </span>
          ))}
        </div>

        {!selectedProperty && (properties?.length || 0) > 1 && (
          <div
            className="flex flex-wrap items-center gap-3 mr-2 text-xs border-l pl-4"
            data-testid="property-color-legend"
          >
            {[...(properties || [])]
              .sort((a, b) => (a.id || '').localeCompare(b.id || ''))
              .map((p) => (
                <span key={p.id} className="flex items-center gap-1.5" title={p.name}>
                  <div
                    className="w-1 h-4 rounded"
                    style={{ background: propertyColorMap[p.id] }}
                  />
                  <span className="text-slate-600 truncate max-w-[10rem]">{p.name}</span>
                </span>
              ))}
          </div>
        )}

        <div className="flex items-center gap-1 text-xs text-slate-400 mr-4 border-l pl-4">
          <GripVertical className="h-3 w-3" />
          <span>Drag to reschedule · Click to edit</span>
        </div>

        <div className="flex border border-slate-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('month')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              viewMode === 'month' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="view-month-btn"
          >
            Month
          </button>
          <button
            onClick={() => setViewMode('week')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              viewMode === 'week' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="view-week-btn"
          >
            Week
          </button>
        </div>
      </div>
    </Card>
  );
};

export default CalendarToolbar;
